import type {CodeownersError, SplitCodeownersError} from '@github-ui/code-view-types'
import type {Repository} from '@github-ui/current-repository'
import {codeownersErrorPath} from '@github-ui/paths'
import type {RefInfo} from '@github-ui/repos-types'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BugIcon, DotFillIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {Dialog, Flash, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useMemo, useRef, useState} from 'react'

import SplitCodeownersErrorsContext from '../contexts/SplitCodeownersErrorsContext'
import styles from './CodeownersValidationBanner.module.css'

const VALID_CODEOWNERS_FILEPATHS = ['CODEOWNERS', '/CODEOWNERS']

const CodeownersValidationState = {
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  VALIDATED: 'VALIDATED',
} as const

type CodeownersValidationState = (typeof CodeownersValidationState)[keyof typeof CodeownersValidationState]

export function CodeownersValidationBanner({
  filePath,
  forceRender,
  full,
  refName,
  repo,
}: {
  filePath: string
  forceRender?: boolean
  full?: boolean
  refName: string
  repo: Pick<Repository, 'name' | 'ownerLogin'>
}) {
  const [validationErrors, setValidationErrors] = useState<SplitCodeownersError[]>([])
  const [validationState, setValidationState] = useState<CodeownersValidationState>(CodeownersValidationState.LOADING)

  const isCodeownersFile = useMemo(() => VALID_CODEOWNERS_FILEPATHS.includes(filePath), [filePath])
  const showBanner = forceRender || isCodeownersFile

  const fetchingDataRef = useRef(false)
  useEffect(() => {
    if (!showBanner) return
    if (fetchingDataRef.current) return

    const getCodeownersValidity = async () => {
      fetchingDataRef.current = true
      try {
        const response = await fetchCodeownersValidity(repo, {name: refName}, filePath)

        if (response.ok) {
          const errors: CodeownersError[] = await response.json()
          setValidationErrors(errors.map(buildSplitCodeownersError))
          setValidationState(CodeownersValidationState.VALIDATED)
        } else {
          setValidationState(CodeownersValidationState.ERROR)
        }
      } catch {
        setValidationState(CodeownersValidationState.ERROR)
      }
    }
    getCodeownersValidity()
  }, [filePath, refName, repo, showBanner])

  if (!showBanner) {
    return null
  }

  return (
    <SplitCodeownersErrorsContext value={validationErrors}>
      <CodeownersValidationBannerInner
        validationErrors={validationErrors}
        validationState={validationState}
        full={full}
      />
    </SplitCodeownersErrorsContext>
  )
}

function CodeownersValidationBannerInner({
  full,
  validationErrors,
  validationState,
}: {
  full?: boolean
  validationErrors: SplitCodeownersError[]
  validationState: CodeownersValidationState
}) {
  const [open, setOpen] = useState(false)
  const expandErrorButtonRef = useRef<HTMLButtonElement>(null)

  if (validationState === CodeownersValidationState.ERROR) {
    return (
      <Flash variant="warning" className={styles.Flash} full={full}>
        Failed to validate this CODEOWNERS file
      </Flash>
    )
  }

  if (validationState === CodeownersValidationState.LOADING) {
    return (
      <Flash variant="default" className={styles.Flash} full={full}>
        Validating CODEOWNERS rules...
      </Flash>
    )
  }

  if (validationErrors.length === 0) {
    return (
      <Flash variant="success" className={styles.Flash} full={full}>
        This CODEOWNERS file is valid.
      </Flash>
    )
  }

  return (
    <>
      <Flash variant="warning" className={styles.Flash_1} full={full}>
        <BugIcon />
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClick={() => setOpen(prev => !prev)} className={styles.Box}>
          This CODEOWNERS file contains errors
          <IconButton
            aria-label="View CODEOWNERS errors"
            tooltipDirection="s"
            ref={expandErrorButtonRef}
            size="small"
            icon={KebabHorizontalIcon}
            className={styles.IconButton}
          />
        </div>
      </Flash>
      {open && (
        <Dialog
          title="CODEOWNERS errors"
          onClose={() => setOpen(false)}
          returnFocusRef={expandErrorButtonRef}
          className={styles.Dialog}
        >
          <div className={styles.Box_1}>
            {validationErrors.map((error, index) => {
              // eslint-disable-next-line @eslint-react/no-array-index-key
              return <CodeownersErrorDetails key={index} error={error} />
            })}
          </div>
        </Dialog>
      )}
    </>
  )
}

function CodeownersErrorDetails({error}: {error: SplitCodeownersError}) {
  return (
    <div className={styles.Box_2}>
      {`${error.kind} on line ${error.line}${error.suggestion ? `: ${error.suggestion}` : ''}`}
      <pre className={clsx(styles.Flash, styles.ErrorCodeBlock)}>
        <code>
          {error.linePrefix}
          <b className={styles.Box_3}>{error.lineError}</b>
          {error.lineSuffix}
        </code>
      </pre>
    </div>
  )
}

const MAX_ERROR_OFFSET = 30
const TRUNCATED_LINE_PREFIX = '…'

function buildSplitCodeownersError(error: CodeownersError): SplitCodeownersError {
  let line = error.source.trim()
  let errorOffset = error.column - 1
  let endOffset = calculateEndOffset(errorOffset, error.end_column, line)

  if (errorOffset > MAX_ERROR_OFFSET) {
    const trimmedCharacters = errorOffset - MAX_ERROR_OFFSET
    line = TRUNCATED_LINE_PREFIX + line.slice(trimmedCharacters)
    errorOffset -= trimmedCharacters - TRUNCATED_LINE_PREFIX.length
    endOffset -= trimmedCharacters - TRUNCATED_LINE_PREFIX.length
  }

  return {
    ...error,
    linePrefix: line.substring(0, errorOffset),
    lineError: line.substring(errorOffset, endOffset),
    lineSuffix: line.substring(endOffset),
  }
}

function calculateEndOffset(errorOffset: number, endColumn: number | null, line: string) {
  if (endColumn) {
    return endColumn
  } else if (line.substring(errorOffset).indexOf(' ') > 0) {
    return line.indexOf(' ', errorOffset)
  } else {
    return line.length
  }
}

export function CodeownersErrorLineIndicator() {
  return <DotFillIcon aria-label="This line contains CODEOWNERS errors" className={styles.Octicon} />
}

export function fetchCodeownersValidity(
  repo: Pick<Repository, 'name' | 'ownerLogin'>,
  refInfo: Pick<RefInfo, 'name'>,
  path: string,
) {
  return verifiedFetchJSON(
    codeownersErrorPath({owner: repo.ownerLogin, repo: repo.name, commitish: refInfo.name, filePath: path}),
    {method: 'GET'},
  )
}
